import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import type { GroupBase } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

/* ----------------------------- DTOs & Props ----------------------------- */

interface LocationDTO { id: number; name: string; }
interface BuildingDTO { id: number; name: string; }
interface FloorDTO { id: number; name: string; }
interface RoomDTO { id: number; name: string; }

export interface User {
  id: number;
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email: string;
  phone: string;
  userType: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  locations: LocationDTO[];
  buildings?: BuildingDTO[]; // not used directly here, we fetch cascading
  floors?: FloorDTO[];
  rooms?: RoomDTO[];
}

/* -------------------------- Users: map & loader -------------------------- */

type UserOption = { value: string; label: string; user: User };

const mapApiUserToDTO = (u: any): User | null => {
  if (!u) return null;

  const apiId = Number(u?.id ?? u?.empNumber);
  if (Number.isNaN(apiId)) return null;

  let expireDate = '';
  const rawExpire = u?.driverSpecificData?.expireDate;
  if (rawExpire) {
    const d = new Date(rawExpire);
    if (!isNaN(d.getTime())) expireDate = d.toISOString().split('T')[0];
  }

  const deptNames: string[] =
    Array.isArray(u?.departmentAdminSpecificData?.departmentNames)
      ? u.departmentAdminSpecificData.departmentNames
      : [];

  return {
    id: apiId,
    empNumber: String(u.empNumber ?? ''),
    name: u?.name ?? u?.fullName ?? u?.email ?? 'Unknown',
    createdAt: u?.createdAt ? new Date(u.createdAt).toISOString() : '',
    status: String(u?.status ?? '').toUpperCase(),
    email: u?.email ?? '',
    phone: u?.phone ?? '',
    userType: String(u?.group ?? 'USER'),
    departmentNames: deptNames,
    licenseNumber: u?.driverSpecificData?.licenseNumber ?? '',
    licenseType: u?.driverSpecificData?.licenseType ?? '',
    expireDate
  };
};

const toOption = (user: User): UserOption => ({
  value: String(user.id),
  label: user.empNumber ? `${user.empNumber} - ${user.name}` : user.name,
  user
});

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];



// Responsible = Minor Staff (unchanged)
const loadMinorStaffOptions = async (inputValue: string): Promise<UserOption[]> => {
  const payload = {
    filterType: inputValue.trim() ? 'nameOrEmpNo' : '',
    filterValue: inputValue.trim() || '',
    date: ''
  };

  const options: UserOption[] = [];
  const seenById = new Set<number>();

  try {
    let page = 1;
    while (true) {
      const res = await apiDefinitions.getUsers(page, payload);
      if (res?.data?.status !== 'S1000') {
        showError(res?.data?.statusDescription || 'Failed to fetch users');
        break;
      }

      const usersArr: any[] = res?.data?.data?.users ?? [];
      if (!usersArr.length) break;

      usersArr
        .filter(u => String(u?.group ?? '').toUpperCase() === 'MINOR_STAFF')
        .map(mapApiUserToDTO)
        .filter((u): u is User => !!u)
        .forEach(u => {
          if (!seenById.has(u.id)) {
            seenById.add(u.id);
            options.push(toOption(u));
          }
        });

      page += 1;
    }
  } catch (e: any) {
    showError(e?.response?.data?.statusDescription || 'Failed to fetch users');
  }

  return options;
};

// Accountable = ALL USERS (new)
const loadAllUsersOptions = async (inputValue: string): Promise<UserOption[]> => {
  const payload = {
    filterType: inputValue.trim() ? 'nameOrEmpNo' : '',
    filterValue: inputValue.trim() || '',
    date: ''
  };

  const options: UserOption[] = [];
  const seenById = new Set<number>();

  try {
    let page = 1;
    while (true) {
      const res = await apiDefinitions.getUsers(page, payload);
      if (res?.data?.status !== 'S1000') {
        showError(res?.data?.statusDescription || 'Failed to fetch users');
        break;
      }

      const usersArr: any[] = res?.data?.data?.users ?? [];
      if (!usersArr.length) break;

      usersArr
        .map(mapApiUserToDTO)
        .filter((u): u is User => !!u)
        .forEach(u => {
          if (!seenById.has(u.id)) {
            seenById.add(u.id);
            options.push(toOption(u));
          }
        });

      page += 1;
    }
  } catch (e: any) {
    showError(e?.response?.data?.statusDescription || 'Failed to fetch users');
  }

  return options;
};

/* -------------------------- Scope resolver (API rule) -------------------------- */
const resolveScopeIds = (
  locationId: number | null,
  buildingId: number | null,
  floorId: number | null,
  roomId: number | null
) => {
  if (roomId) return { locationId: null, buildingId: null, floorId: null, roomId };
  if (floorId) return { locationId: null, buildingId: null, floorId, roomId: null };
  if (buildingId) return { locationId: null, buildingId, floorId: null, roomId: null };
  if (locationId) return { locationId, buildingId: null, floorId: null, roomId: null };
  return null;
};

/* ------------------------------ Main module ------------------------------ */

const AddMaintenanceTaskModule: React.FC<Props> = ({
  show,
  onHide,
  onSuccess,
  locations,
}) => {
  const [description, setDescription] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  const [buildingsLocal, setBuildingsLocal] = useState<BuildingDTO[]>([]);
  const [floorsLocal, setFloorsLocal] = useState<FloorDTO[]>([]);
  const [roomsLocal, setRoomsLocal] = useState<RoomDTO[]>([]);

  const [startDateTime, setStartDateTime] = useState<Date | null>(new Date());
  const [endDateTime, setEndDateTime] = useState<Date | null>(null);

  const [isRecurring, setIsRecurring] = useState(false);
  const [scheduleType, setScheduleType] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [occurrenceCount, setOccurrenceCount] = useState<number | null>(null);

  const [isPermanentResponsibleAllocate, setIsPermanentResponsibleAllocate] = useState(false);
  const [responsiblePersons, setResponsiblePersons] = useState<User[]>([]);
  const [accountablePersons, setAccountablePersons] = useState<User[]>([]);

  const [isSaving, setIsSaving] = useState(false);

  const resetForm = () => {
    setDescription('');
    setSelectedLocationId(null);
    setSelectedBuildingId(null);
    setSelectedFloorId(null);
    setSelectedRoomId(null);
    setBuildingsLocal([]);
    setFloorsLocal([]);
    setRoomsLocal([]);
    setStartDateTime(new Date());
    setEndDateTime(null);
    setIsRecurring(false);
    setScheduleType(null);
    setSelectedDays([]);
    setOccurrenceCount(null);
    setIsPermanentResponsibleAllocate(false);
    setResponsiblePersons([]);
    setAccountablePersons([]);
  };

  useEffect(() => {
    if (!show) resetForm();
  }, [show]);

  const fetchBuildingsForLocation = async (locId: number) => {
    try {
      const res = await apiDefinitions.getBuildingsByLocationId(locId);
      if (res.status === 200) {
        setBuildingsLocal(res.data?.data || []);
        setSelectedBuildingId(null);
        setFloorsLocal([]); setSelectedFloorId(null);
        setRoomsLocal([]); setSelectedRoomId(null);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch buildings');
    }
  };

  const fetchFloorsForBuilding = async (bId: number) => {
    try {
      const res = await apiDefinitions.getFloorsByBuildingId(bId);
      if (res.status === 200) {
        setFloorsLocal(res.data?.data || []);
        setSelectedFloorId(null);
        setRoomsLocal([]); setSelectedRoomId(null);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch floors');
    }
  };

  const fetchRoomsForFloor = async (fId: number) => {
    try {
      const res = await apiDefinitions.getRoomsByFloorId(fId);
      if (res.status === 200) {
        setRoomsLocal(res.data?.data || []);
        setSelectedRoomId(null);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch rooms');
    }
  };

  useEffect(() => {
    if (selectedLocationId) fetchBuildingsForLocation(selectedLocationId);
    else {
      setBuildingsLocal([]); setSelectedBuildingId(null);
      setFloorsLocal([]); setSelectedFloorId(null);
      setRoomsLocal([]); setSelectedRoomId(null);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (selectedBuildingId) fetchFloorsForBuilding(selectedBuildingId);
    else {
      setFloorsLocal([]); setSelectedFloorId(null);
      setRoomsLocal([]); setSelectedRoomId(null);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedFloorId) fetchRoomsForFloor(selectedFloorId);
    else {
      setRoomsLocal([]); setSelectedRoomId(null);
    }
  }, [selectedFloorId]);

  const handleDaySelection = (day: string) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  // When scheduleType switches, set defaults for selectedDays
  useEffect(() => {
    if (!isRecurring) {
      setSelectedDays([]);
      return;
    }
    if (scheduleType === 'WEEKDAYS') {
      setSelectedDays(WEEKDAYS);
    } else if (scheduleType === 'WEEKENDS') {
      setSelectedDays(WEEKENDS);
    } else if (scheduleType === 'CUSTOM') {
      setSelectedDays(prev => prev.length ? prev : []);
    } else {
      setSelectedDays([]); // DAILY/WEEKLY/MONTHLY/YEARLY don’t require days list
    }
  }, [isRecurring, scheduleType]);

  const handleSubmit = async () => {
    if (!checkPermission('ROLE_ADD_TASK')) {
      showError('You do not have permission to add maintenance tasks.');
      return;
    }

    if (!description || !startDateTime) {
      showError('Please fill in all required fields (Description and Start Date & Time).');
      return;
    }

    const scope = resolveScopeIds(
      selectedLocationId, selectedBuildingId, selectedFloorId, selectedRoomId
    );
    if (!scope) {
      showError('Please select a location/building/floor/room (deepest selection wins).');
      return;
    }

    if (isPermanentResponsibleAllocate && (responsiblePersons.length === 0 || accountablePersons.length === 0)) {
      showError('Please select at least one responsible and one accountable person.');
      return;
    }

    if (isPermanentResponsibleAllocate && responsiblePersons.some(rp => accountablePersons.includes(rp))) {
      showError('Responsible and Accountable persons must be different.');
      return;
    }

    let customDays: string | null = null;
    if (isRecurring) {
      if (scheduleType === 'WEEKDAYS') {
        customDays = WEEKDAYS.join(',');
      } else if (scheduleType === 'WEEKENDS') {
        customDays = WEEKENDS.join(',');
      } else if (scheduleType === 'CUSTOM') {
        if (!selectedDays.length) {
          showError('Please select at least one day for Custom schedule.');
          return;
        }
        customDays = selectedDays.join(',');
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        description,
        startDateTime: startDateTime?.toISOString(),
        endDateTime: endDateTime?.toISOString() || null,

        isRecurring,
        scheduleType: isRecurring ? scheduleType : null,
        customDays: isRecurring ? customDays : null,

        occurrenceCount: isRecurring ? (occurrenceCount ?? null) : null,

        locationId: scope.locationId,
        buildingId: scope.buildingId,
        floorId: scope.floorId,
        roomId: scope.roomId,

        isPermanentResponsibleAllocate,
        responsiblePersons: responsiblePersons.map(rp => rp.id), // Array of IDs
        accountablePersons: accountablePersons.map(ap => ap.id), // Array of IDs
      };

      const response = await apiDefinitions.createMaintenanceTask(payload);
      if (response?.data?.status === 'S1000') {
        showSuccess("Maintenance task created successfully");
        onSuccess();
        resetForm();
        onHide();
      } else {
        showError(response?.data?.statusDescription || 'Failed to create maintenance task');
      }
    } catch (error: any) {
      showError(error?.response?.data?.statusDescription || 'Error creating maintenance task');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Maintenance Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            {/* Left */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  disabled={isSaving}
                />
              </Form.Group>

              {/* Cascading */}
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Select
                  value={selectedLocationId ?? ''}
                  onChange={(e) => setSelectedLocationId(Number(e.target.value) || null)}
                  disabled={locations.length === 0 || isSaving}
                >
                  <option value="">Select a location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Building</Form.Label>
                <Form.Select
                  value={selectedBuildingId ?? ''}
                  onChange={(e) => setSelectedBuildingId(Number(e.target.value) || null)}
                  disabled={buildingsLocal.length === 0 || !selectedLocationId || isSaving}
                >
                  <option value="">Select a building</option>
                  {buildingsLocal.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Floor</Form.Label>
                <Form.Select
                  value={selectedFloorId ?? ''}
                  onChange={(e) => setSelectedFloorId(Number(e.target.value) || null)}
                  disabled={floorsLocal.length === 0 || !selectedBuildingId || isSaving}
                >
                  <option value="">Select a floor</option>
                  {floorsLocal.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Room</Form.Label>
                <Form.Select
                  value={selectedRoomId ?? ''}
                  onChange={(e) => setSelectedRoomId(Number(e.target.value) || null)}
                  disabled={roomsLocal.length === 0 || !selectedFloorId || isSaving}
                >
                  <option value="">Select a room</option>
                  {roomsLocal.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Right */}
            <Col md={6}>
              {/* Dates */}
              <Form.Group className="mb-3">
                <Form.Label>Start Date & Time</Form.Label>
                <DatePicker
                  selected={startDateTime}
                  onChange={(d: Date | null) => setStartDateTime(d)}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  required
                  disabled={isSaving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>End Date & Time</Form.Label>
                <DatePicker
                  selected={endDateTime}
                  onChange={(d: Date | null) => setEndDateTime(d)}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  disabled={isSaving}
                />
              </Form.Group>

              {/* Recurrence */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Is Recurring"
                  checked={isRecurring}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsRecurring(checked);
                    if (!checked) {
                      setScheduleType(null);
                      setSelectedDays([]);
                      setOccurrenceCount(null);
                    }
                  }}
                  disabled={isSaving}
                />
              </Form.Group>

              {isRecurring && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Schedule Type</Form.Label>
                    <Form.Select
                      value={scheduleType ?? ''}
                      onChange={(e) => setScheduleType(e.target.value || null)}
                      disabled={isSaving}
                    >
                      <option value="">Select schedule type</option>
                      {['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY', 'WEEKDAYS', 'WEEKENDS', 'CUSTOM'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* WEEKDAYS / WEEKENDS -> show a preview pill (auto-sent) */}
                  {(scheduleType === 'WEEKDAYS' || scheduleType === 'WEEKENDS') && (
                    <Form.Group className="mb-3">
                      <Form.Label>Days</Form.Label>
                      <div>
                        {(scheduleType === 'WEEKDAYS' ? WEEKDAYS : WEEKENDS).map(day => (
                          <span key={day} className="badge bg-secondary me-2">{day}</span>
                        ))}
                      </div>
                      <Form.Text muted>
                        These days will be sent automatically as <code>customDays</code>.
                      </Form.Text>
                    </Form.Group>
                  )}

                  {/* CUSTOM -> choose any of the 7 days */}
                  {scheduleType === 'CUSTOM' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Days</Form.Label>
                      <div>
                        {DAYS.map(day => (
                          <Form.Check
                            key={day}
                            type="checkbox"
                            label={day}
                            checked={selectedDays.includes(day)}
                            onChange={() => handleDaySelection(day)}
                            disabled={isSaving}
                            inline
                          />
                        ))}
                      </div>
                    </Form.Group>
                  )}

                  {/* Optional: occurrence count */}
                  <Form.Group className="mb-3">
                    <Form.Label>Occurrence Count (optional)</Form.Label>
                    <Form.Control
                      type="number"
                      value={occurrenceCount ?? ''}
                      onChange={(e) => setOccurrenceCount(Number(e.target.value) || null)}
                      min="1"
                      disabled={isSaving}
                    />
                  </Form.Group>
                </>
              )}

              {/* Responsible / Accountable */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Permanent Responsible Allocation"
                  checked={isPermanentResponsibleAllocate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsPermanentResponsibleAllocate(checked);
                    if (!checked) {
                      setResponsiblePersons([]);
                      setAccountablePersons([]);
                    }
                  }}
                  disabled={isSaving}
                />
              </Form.Group>

              {isPermanentResponsibleAllocate && (
                <>
                  {/* Responsible: Minor Staff */}
                  <Form.Group className="mb-3">
                    <Form.Label>Responsible Person(s)</Form.Label>
                    <AsyncSelect<UserOption, true, GroupBase<UserOption>>
                      cacheOptions
                      defaultOptions
                      loadOptions={loadMinorStaffOptions}
                      onChange={(selectedOptions) => {
                        const users = selectedOptions ? selectedOptions.map(opt => opt.user) : [];
                        setResponsiblePersons(users);
                      }}
                      placeholder="Search by EMP No. or Name"
                      value={responsiblePersons.map(u => ({
                        value: String(u.id),
                        label: `${u.empNumber} - ${u.name}`,
                        user: u
                      }))}
                      getOptionValue={(o) => o.value}
                      getOptionLabel={(o) => o.label}
                      isMulti
                      isClearable
                      isDisabled={isSaving}
                      noOptionsMessage={() => 'No minor staff found'}
                      loadingMessage={() => 'Loading minor staff...'}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>

                  {/* Accountable: ALL USERS */}
                  <Form.Group className="mb-3">
                    <Form.Label>Accountable Person(s)</Form.Label>
                    <AsyncSelect<UserOption, true, GroupBase<UserOption>>
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAllUsersOptions}
                      onChange={(selectedOptions) => {
                        const users = selectedOptions ? selectedOptions.map(opt => opt.user) : [];
                        setAccountablePersons(users);
                      }}
                      placeholder="Search by EMP No. or Name"
                      value={accountablePersons.map(u => ({
                        value: String(u.id),
                        label: `${u.empNumber} - ${u.name}`,
                        user: u
                      }))}
                      getOptionValue={(o) => o.value}
                      getOptionLabel={(o) => o.label}
                      isMulti
                      isClearable
                      isDisabled={isSaving}
                      noOptionsMessage={() => 'No users found'}
                      loadingMessage={() => 'Loading users...'}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Form.Group>
                </>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onHide} disabled={isSaving}>
          Close
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMaintenanceTaskModule;

